import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import api, { API, formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Eye, Trash2, CheckCircle2, RefreshCw, FileClock, Plus, Info } from "lucide-react";
import { toast } from "sonner";

export default function MRDrafts() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewId, setPreviewId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [quota, setQuota] = useState({ used: 0, limit: 6, remaining: 6 });

  const loadDrafts = () => {
    setLoading(true);
    api.get("/requests?status=draft")
      .then(({ data }) => setDrafts(data))
      .catch(() => toast.error("Failed to load drafts"))
      .finally(() => setLoading(false));
  };

  const loadQuota = () => {
    api.get("/requests/quota").then(({ data }) => setQuota(data)).catch(() => {});
  };

  useEffect(() => {
    loadDrafts();
    loadQuota();
  }, []);

  const submitDraft = async (r) => {
    if (quota.remaining <= 0) {
      toast.error(`You have reached your ${quota.limit} request limit. Please ask admin to unlock your ID.`);
      return;
    }
    if (!window.confirm(`Submit the certificate for Dr. ${r.doctor_name}? This uses one of your ${quota.limit} request slots.`)) return;
    setBusyId(r.id);
    try {
      const { data } = await api.post(`/requests/${r.id}/submit`);
      setDrafts((prev) => prev.filter((d) => d.id !== r.id));
      toast.success(`Request ${data.display_id} submitted!`);
      loadQuota();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Discard this draft? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await api.delete(`/requests/${id}`);
      setDrafts((prev) => prev.filter((r) => r.id !== id));
      toast.success("Draft discarded");
    } catch {
      toast.error("Failed to discard draft");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Shell role="mr">
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">Drafts</div>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Your Draft Certificates</h1>
          <p className="text-slate-500 mt-1 text-sm">Review each preview and submit it to send the request to the admin team.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600">
            <span className="font-semibold">Quota:</span>
            <span>{quota.used}/{quota.limit} used</span>
            <span className="text-slate-400">|</span>
            <span>{quota.remaining} left</span>
          </div>
          <Link to="/mr/generate">
            <Button className="h-10 px-4 bg-brand-800 hover:bg-brand-700 gap-2">
              <Plus className="w-4 h-4" /> New Certificate
            </Button>
          </Link>
        </div>
      </div>

      {/* Info banner */}
      <div className="flex items-start gap-2 p-3 mb-6 rounded-lg bg-blue-50 border border-blue-100 text-blue-700">
        <Info className="w-4 h-4 mt-0.5 shrink-0" />
        <div className="text-xs leading-relaxed">
          A <b>draft is not yet submitted</b> and nothing is counted against your quota until you submit it.
          Review the certificate preview here and click <b>Submit</b> to send it for printing &amp; dispatch.
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : drafts.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 grid place-items-center mb-3">
            <FileClock className="w-6 h-6 text-slate-500" />
          </div>
          <div className="font-display text-lg font-bold text-slate-900 mb-1">No drafts</div>
          <div className="text-sm text-slate-500 mb-4">Generate a certificate preview to create a draft you can review and submit.</div>
          <Link to="/mr/generate"><Button>Create Certificate</Button></Link>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {drafts.map((r) => {
            const isBusy = busyId === r.id;
            return (
              <Card key={r.id} className="overflow-hidden flex flex-col">
                <button
                  type="button"
                  className="aspect-[3451/2401] bg-slate-100 w-full block relative group"
                  onClick={() => setPreviewId(r.id)}
                >
                  <img src={`${API}/requests/${r.id}/preview`} alt={r.doctor_name} className="w-full h-full object-cover" />
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-brand-800/80 text-white text-[10px] font-semibold uppercase tracking-wider">Draft</span>
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition grid place-items-center text-white opacity-0 group-hover:opacity-100">
                    <Eye className="w-6 h-6" />
                  </span>
                </button>
                <div className="p-3 space-y-2 flex-1 flex flex-col">
                  <div className="text-sm font-semibold text-slate-900 truncate">Dr. {r.doctor_name}</div>
                  <div className="text-xs text-slate-500 truncate">{r.speciality || "-"}</div>
                  <div className="text-[11px] text-slate-400">{new Date(r.created_at).toLocaleString()}</div>
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    className="w-full h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                    onClick={() => submitDraft(r)}
                    disabled={isBusy || quota.remaining <= 0}
                  >
                    {isBusy ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Submitting…</> : <><CheckCircle2 className="w-3.5 h-3.5" /> Submit Request</>}
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    className="w-full h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 gap-1"
                    onClick={() => handleDelete(r.id)}
                    disabled={isBusy}
                  >
                    <Trash2 className="w-3 h-3" /> Discard
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {quota.remaining <= 0 && drafts.length > 0 && (
        <p className="text-xs text-amber-700 text-center mt-4">
          You have used all {quota.limit} request slots. Ask the admin to unlock your ID to submit more.
        </p>
      )}

      {/* Preview dialog */}
      <Dialog open={!!previewId} onOpenChange={(o) => !o && setPreviewId(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Draft Preview</DialogTitle>
          </DialogHeader>
          {previewId && (
            <img
              src={`${API}/requests/${previewId}/preview?t=${Date.now()}`}
              alt="Draft preview"
              className="w-full rounded-md border border-slate-200"
            />
          )}
          <p className="text-xs text-slate-400 text-center">This is a draft — submit it from the drafts list to send the request.</p>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
